import { useState } from 'react';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useWaitingVariantTracking } from '../useWaitingVariantTracking';
import { shuffleItems } from '../waitingVariants';

export type QuizQuestion = {
	question: string;
	options: string[];
	correctOption: string;
	funFact: string;
};

export type QuizBank = {
	id: string;
	title: string;
	emoji: string;
	questions: QuizQuestion[];
};

const QUESTIONS_PER_ROUND = 5;

const buildRound = (bank: QuizBank): QuizQuestion[] =>
	shuffleItems(bank.questions)
		.slice(0, QUESTIONS_PER_ROUND)
		.map((question) => ({
			...question,
			options: shuffleItems(question.options),
		}));

const getScoreMessage = (score: number, total: number): string => {
	if (score === total) return '🏆 ¡Nivel contador! Te sabes todas.';
	if (score >= Math.ceil(total * 0.6))
		return '🎉 ¡Muy bien! Ya dominas lo básico del SAT.';
	if (score >= 1) return '💪 ¡Buen intento! Cada respuesta te deja algo nuevo.';
	return '😅 ¡Para eso está heru! Nosotros nos encargamos de lo fiscal.';
};

// Motor de quiz: arma rondas a partir de bancos temáticos y rota de banco en
// cada "Jugar otra vez". El contenido vive en content/quizBanks.ts.
const QuizEngine = ({ banks }: { banks: QuizBank[] }) => {
	const { trackInteraction } = useWaitingVariantTracking();
	const [bankIndex, setBankIndex] = useState(() =>
		Math.floor(Math.random() * banks.length),
	);
	const [round, setRound] = useState<QuizQuestion[]>(() =>
		buildRound(banks[bankIndex]),
	);
	const [questionIndex, setQuestionIndex] = useState(0);
	const [selectedOption, setSelectedOption] = useState<string | null>(null);
	const [score, setScore] = useState(0);
	const [isFinished, setIsFinished] = useState(false);

	const bank = banks[bankIndex];
	const currentQuestion = round[questionIndex];
	const isAnswered = selectedOption !== null;
	const isLastQuestion = questionIndex === round.length - 1;

	const handleSelectOption = (option: string) => {
		if (isAnswered) return;
		setSelectedOption(option);
		const isCorrect = option === currentQuestion.correctOption;
		trackInteraction('quiz_answered', { bank: bank.id, correct: isCorrect });
		if (isCorrect) {
			setScore((prev) => prev + 1);
		}
	};

	const handleNextQuestion = () => {
		if (isLastQuestion) {
			trackInteraction('quiz_completed', {
				bank: bank.id,
				score,
				total: round.length,
			});
			setIsFinished(true);
			return;
		}
		setQuestionIndex((prev) => prev + 1);
		setSelectedOption(null);
	};

	const handlePlayAgain = () => {
		const nextBankIndex = (bankIndex + 1) % banks.length;
		trackInteraction('quiz_replayed', { bank: banks[nextBankIndex].id });
		setBankIndex(nextBankIndex);
		setRound(buildRound(banks[nextBankIndex]));
		setQuestionIndex(0);
		setSelectedOption(null);
		setScore(0);
		setIsFinished(false);
	};

	if (isFinished) {
		return (
			<div className='flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-xl bg-h-primary-95 px-5 py-6 text-center'>
				<span className='text-4xl'>🎓</span>
				<h3 className='text-base font-semibold text-h-text-heading'>
					Acertaste {score} de {round.length}
				</h3>
				<p className='text-sm text-h-neutral-50'>
					{getScoreMessage(score, round.length)}
				</p>
				<Button
					onClick={handlePlayAgain}
					className='bg-h-primary-50 mt-1 h-11 rounded-xl px-6 font-semibold text-white'
				>
					Jugar otra vez
				</Button>
			</div>
		);
	}

	const getOptionClassName = (option: string): string => {
		if (!isAnswered) {
			return 'border-h-neutral-80 bg-white text-h-text-heading';
		}
		if (option === currentQuestion.correctOption) {
			return 'border-h-success-50 bg-h-success-95 text-h-success-30';
		}
		if (option === selectedOption) {
			return 'border-h-error-50 bg-h-error-95 text-h-error-50';
		}
		return 'border-h-neutral-80 bg-white text-h-text-heading opacity-50';
	};

	return (
		<div className='flex flex-col gap-3 rounded-xl bg-h-primary-95 px-4 py-5'>
			<div className='flex items-center justify-between'>
				<p className='text-[11px] font-bold uppercase tracking-[0.08em] text-h-primary-40'>
					{bank.emoji} {bank.title}
				</p>
				<p className='text-xs text-h-neutral-50'>
					{questionIndex + 1} de {round.length}
				</p>
			</div>
			<h3 className='text-base font-semibold text-h-text-heading leading-snug'>
				{currentQuestion.question}
			</h3>
			<div className='flex flex-col gap-2'>
				{currentQuestion.options.map((option) => (
					<Button
						key={option}
						variant='outline'
						onClick={() => handleSelectOption(option)}
						className={`h-auto min-h-11 w-full justify-start whitespace-normal rounded-lg border px-3 py-2.5 text-left text-sm font-medium ${getOptionClassName(option)}`}
					>
						{isAnswered && option === currentQuestion.correctOption && (
							<CheckCircle
								size={18}
								weight='fill'
								className='shrink-0 text-h-success-50'
							/>
						)}
						{isAnswered &&
							option === selectedOption &&
							option !== currentQuestion.correctOption && (
								<XCircle
									size={18}
									weight='fill'
									className='shrink-0 text-h-error-50'
								/>
							)}
						{option}
					</Button>
				))}
			</div>
			{isAnswered && (
				<div className='flex flex-col gap-2'>
					<p className='text-xs text-h-neutral-50 leading-snug'>
						{currentQuestion.funFact}
					</p>
					<Button
						onClick={handleNextQuestion}
						className='bg-h-primary-50 h-11 w-full rounded-xl font-semibold text-white'
					>
						{isLastQuestion ? 'Ver mi resultado' : 'Siguiente'}
					</Button>
				</div>
			)}
		</div>
	);
};

export default QuizEngine;
